/**
 * The grid's keyboard: move the cursor, act on the active cell, clear it,
 * and copy and paste, leaving the marks those leave (see useCellMarks).
 */

import type React from 'react';
import { type RefObject, useState } from 'react';
import type { Column, Row } from '@tanstack/react-table';
import { dispatchCellType, findTextCell, focusOutsideGrid } from './dom';
import { type CellMarks, NEW_ROW_ID } from './useCellMarks';
import type { CellKey, Cursor, SpreadsheetProps } from './types';

type KeyLike = Pick<
  React.KeyboardEvent,
  'key' | 'shiftKey' | 'metaKey' | 'ctrlKey'
>;

/** Where a navigation key sends the cursor, or null for any other key. */
export function navigationTarget(
  e: KeyLike,
  { row, col }: Cursor,
  rowCount: number,
  colCount: number
): Cursor | null {
  // Cmd (Ctrl elsewhere) with an arrow jumps to that edge of the grid.
  const jump = e.metaKey || e.ctrlKey;
  switch (e.key) {
    case 'ArrowUp':
      return { row: jump ? 0 : row - 1, col };
    case 'ArrowDown':
      return { row: jump ? rowCount - 1 : row + 1, col };
    case 'ArrowLeft':
      return { row, col: jump ? 0 : col - 1 };
    case 'ArrowRight':
      return { row, col: jump ? colCount - 1 : col + 1 };
    case 'Tab': {
      // Past either end of the row, Tab leaves the grid (see the handler).
      const next = col + (e.shiftKey ? -1 : 1);
      return next < 0 || next >= colCount ? null : { row, col: next };
    }
    case 'Home':
      return { row, col: 0 };
    case 'End':
      return { row, col: colCount - 1 };
    case 'PageDown':
      return { row: row + 10, col };
    case 'PageUp':
      return { row: row - 10, col };
    default:
      return null;
  }
}

/**
 * Whether a key pressed on `target` is the grid's. React carries key events
 * up through portals, so a cell's open dropdown or dialog would steer the
 * grid too; only keys pressed in the grid count. The header's own buttons
 * (sort, filter) are inside the scroller, and an editor keeps its own keys.
 */
function isGridKey(target: HTMLElement, root: HTMLElement | null): boolean {
  if (!root?.contains(target) || target.closest('thead')) return false;
  return !(
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  );
}

const isCopyOrPaste = (e: React.KeyboardEvent, key: 'c' | 'v') =>
  (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === key;

const isClear = (e: React.KeyboardEvent) =>
  e.key === 'Backspace' || e.key === 'Delete';

// Without the clipboard API (an insecure page) a copy or paste is refused,
// as one the browser denies is.
const readClipboard = (): Promise<string> =>
  navigator.clipboard
    ? navigator.clipboard.readText()
    : Promise.reject(new Error('No clipboard'));
const writeClipboard = (text: string): Promise<void> =>
  navigator.clipboard
    ? navigator.clipboard.writeText(text)
    : Promise.reject(new Error('No clipboard'));

type CellKeysOptions<T> = Pick<
  SpreadsheetProps<T>,
  | 'getRowId'
  | 'onClearCell'
  | 'onCopyStructured'
  | 'onPasteStructured'
  | 'newRow'
> & {
  rootRef: RefObject<HTMLElement | null>;
  activeCellRef: RefObject<HTMLTableCellElement | null>;
  cursor: Cursor;
  moveTo: (cursor: Cursor) => void;
  /** The data rows, in the order drawn. */
  rows: Row<T>[];
  /** Every row the cursor can reach: data rows, the stand-in, the blank row. */
  rowCount: number;
  leafColumns: Column<T, unknown>[];
  /** The stand-in's row index, or -1. */
  standInIndex: number;
  /** The blank row's index, or -1. */
  newRowIndex: number;
  createRow: (columnId: string, value: string) => void;
  canEditRow: (row: T) => boolean;
  marks: CellMarks;
};

export function useCellKeys<T>({
  rootRef,
  activeCellRef,
  cursor,
  moveTo,
  rows,
  rowCount,
  leafColumns,
  standInIndex,
  newRowIndex,
  newRow,
  createRow,
  getRowId,
  canEditRow,
  onClearCell,
  onCopyStructured,
  onPasteStructured,
  marks: { copied, setCopied, refuse },
}: CellKeysOptions<T>): (e: React.KeyboardEvent<HTMLElement>) => void {
  // A structured value copied from the grid, kept in the page rather than the
  // system clipboard.
  const [structuredClip, setStructuredClip] = useState<{
    kind: string;
    value: unknown;
  } | null>(null);

  /**
   * Enter, Space or F2 clicks the active cell's button; Enter and F2 (never
   * Space) fall back to its link. A button outranks a link in the same cell.
   */
  const activate = (e: React.KeyboardEvent): boolean => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'F2') return false;
    // On a text cell a space is typing, handled by `typeInto`.
    if (e.key === ' ' && findTextCell(activeCellRef.current)) return false;
    e.preventDefault();
    const cell = activeCellRef.current;
    const button = cell?.querySelector<HTMLElement>('button, [role="button"]');
    if (button) button.click();
    else if (e.key !== ' ')
      cell?.querySelector<HTMLElement>('a[href]')?.click();
    return true;
  };

  /**
   * A printable key on a text cell starts editing with that character. On a
   * row that takes no writes it flashes instead, as a paste or clear would.
   */
  const typeInto = (e: React.KeyboardEvent): boolean => {
    if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return false;
    const textCell = findTextCell(activeCellRef.current);
    if (!textCell) return false;
    e.preventDefault();
    const row = rows[cursor.row]?.original;
    const column = leafColumns[cursor.col];
    if (row && column && !canEditRow(row)) {
      refuse(getRowId(row), column.id);
      return true;
    }
    dispatchCellType(textCell, e.key);
    return true;
  };

  /**
   * The blank row: a paste into one of its text columns adds the row;
   * nothing else applies to a row that does not exist yet.
   */
  const onBlankRowKey = (e: React.KeyboardEvent, columnId: string) => {
    if (isCopyOrPaste(e, 'v')) {
      e.preventDefault();
      if (!newRow?.columns.includes(columnId)) {
        refuse(NEW_ROW_ID, columnId);
        return;
      }
      readClipboard()
        .then(text => createRow(columnId, text))
        .catch(() => refuse(NEW_ROW_ID, columnId));
    } else if (isClear(e)) {
      e.preventDefault();
    }
  };

  const copy = (e: React.KeyboardEvent, row: T, column: Column<T, unknown>) => {
    const meta = column.columnDef.meta;
    const key: CellKey = { rowId: getRowId(row), columnId: column.id };
    if (meta?.structured && onCopyStructured) {
      e.preventDefault();
      const { kind } = meta.structured;
      setStructuredClip({ kind, value: onCopyStructured(row, kind) });
    } else if (meta?.clipboard) {
      e.preventDefault();
      writeClipboard(meta.clipboard.copy(row)).catch(() => {
        // Nothing was copied after all: take the mark back and say so.
        setCopied(current =>
          current?.rowId === key.rowId && current.columnId === key.columnId
            ? null
            : current
        );
        refuse(key.rowId, key.columnId);
      });
    } else {
      return;
    }
    setCopied(key);
  };

  const paste = (
    e: React.KeyboardEvent,
    row: T,
    column: Column<T, unknown>
  ) => {
    e.preventDefault();
    const meta = column.columnDef.meta;
    const rowId = getRowId(row);
    const editable = canEditRow(row);
    if (meta?.structured) {
      const { kind } = meta.structured;
      if (!editable || !onPasteStructured || structuredClip?.kind !== kind) {
        refuse(rowId, column.id);
        return;
      }
      onPasteStructured(row, kind, structuredClip.value);
      return;
    }
    const write = meta?.clipboard?.paste;
    const canPaste = meta?.clipboard?.canPaste?.(row) ?? true;
    if (!editable || !write || !canPaste) {
      // Read-only cells, or a row that takes no writes.
      refuse(rowId, column.id);
      return;
    }
    readClipboard()
      .then(text => write(row, text))
      .catch(() => refuse(rowId, column.id));
  };

  return e => {
    if (!isGridKey(e.target as HTMLElement, rootRef.current)) return;
    if (!rowCount) return;

    const next = navigationTarget(e, cursor, rowCount, leafColumns.length);
    if (next) {
      e.preventDefault();
      moveTo(next);
      return;
    }
    if (e.key === 'Tab') {
      // Past the first or last cell: on to whatever comes before or after the
      // grid, not into its own header buttons or cell controls.
      const root = rootRef.current;
      if (root && focusOutsideGrid(root, e.shiftKey ? -1 : 1)) {
        e.preventDefault();
      }
      return;
    }
    if (activate(e) || typeInto(e)) return;
    // Anywhere, the blank and stand-in rows included.
    if (e.key === 'Escape' && copied) {
      e.preventDefault();
      setCopied(null);
      return;
    }

    const column = leafColumns[cursor.col];
    // The stand-in takes nothing until the real row replaces it.
    if (!column || cursor.row === standInIndex) return;
    if (cursor.row === newRowIndex) {
      onBlankRowKey(e, column.id);
      return;
    }
    const row = rows[cursor.row]?.original;
    if (!row) return;

    if (isClear(e)) {
      e.preventDefault();
      const cleared = canEditRow(row) && !!onClearCell?.(row, column.id);
      if (!cleared) refuse(getRowId(row), column.id);
    } else if (isCopyOrPaste(e, 'c')) {
      copy(e, row, column);
    } else if (isCopyOrPaste(e, 'v')) {
      paste(e, row, column);
    }
  };
}
